/**
 * Screen tokens for the guest decline view (`/decline/request?id=…&signer=true`)
 * — the "Decline Request" frame of `Signer Experience.dc.html`.
 *
 * Every colour, size, radius, spacing and type value below is read straight off
 * that frame; `DeclineRequestPage` consumes these instead of literals. Kept
 * separate from `SignerExperience.tokens.ts` even where values coincide — the
 * two frames are re-skinned independently (see UI_IMPLEMENTATION_RULES.md
 * § Temporary Screen Tokens / Do Not Force Consistency).
 */

import { formField, signer, white } from "theme/colors";

const palette = {
  ink: signer.ink,
  body: signer.text.secondary,
  meta: signer.text.meta,
  muted: signer.text.faint,
  placeholder: formField.placeholderMuted,
  disabledInk: signer.border.strong,

  white,
  canvas: signer.surface.canvas,
  hover: signer.surface.subtle,
  railHover: signer.surface.muted,

  borderLight: signer.border.light,
  border: signer.border.main,
  borderStrong: signer.border.strong,

  accent: signer.orange.main,
  accentTint: signer.orange.tint,

  danger: signer.danger.main,
  dangerText: signer.danger.dark,
  dangerBorder: signer.danger.border,
  dangerBorderStrong: signer.danger.borderStrong,
  dangerTint: signer.danger.tint,

  /** Submit button fill while the reason is still empty. */
  disabledFill: signer.disabled,
};

const declineRequestTokens = {
  palette,

  /**
   * The design mock sets no `line-height`, so every text node runs on the
   * font's `normal`. MUI's `body1` forces 1.5 and CssBaseline inherits it into
   * the textarea — restate this anywhere the box is sized by its text.
   */
  lineHeight: "normal",

  /** Back button + title block. */
  header: {
    height: 64,
    paddingX: "20px",
    gap: "14px",
    background: palette.white,
    borderBottom: `1px solid ${palette.borderLight}`,

    backSize: 38,
    backRadius: "9px",
    backIconSize: 18,
    backColor: palette.body,
    backHoverBg: palette.railHover,

    titleGap: "1px",
    titleFontSize: "15px",
    titleFontWeight: 800,
    titleColor: palette.ink,
    subtitleFontSize: "11.5px",
    subtitleColor: palette.meta,
  },

  /** Scrolling document canvas to the left of the rail. */
  canvas: {
    background: palette.canvas,
    paddingTop: "28px",
    paddingX: "36px",
    /**
     * The design pads 60px below the sheet because its mock document is short
     * and the whole pane scrolls. The real PDF viewer owns its own scroll
     * container and must be height-bounded, so the sheet fills the pane and
     * this is the gap under it instead.
     */
    paddingBottom: "28px",
    contentMaxWidth: 760,

    /** Document switcher row above the sheet. */
    toolbarGap: "8px",
    toolbarMarginBottom: "14px",

    navButtonSize: 32,
    navButtonRadius: "8px",
    navButtonBorder: `1px solid ${palette.border}`,
    navIconSize: 11,

    docButtonHeight: 34,
    docButtonMaxWidth: 420,
    docButtonPaddingX: "12px",
    docButtonRadius: "8px",
    docButtonBorderWidth: "1.5px",
    docButtonGap: "9px",
    docButtonFontSize: "12.5px",
    docButtonFontWeight: 700,
    docIconSize: 14,
    docFlagDotSize: 7,
    docPosFontSize: "11px",
    docPosFontWeight: 700,
    caretSize: 11,

    hintFontSize: "11.5px",
    hintColor: palette.muted,

    /**
     * The white sheet the PDF renders into. The design pads it 52px/60px for
     * its mock clause list; a rendered PDF page carries its own margins, so the
     * sheet is flush here.
     */
    sheetRadius: "12px",
    sheetBorder: `1px solid ${palette.border}`,
    sheetShadow: "0 6px 24px rgba(33, 21, 71, 0.06)",
  },

  /** Document picker opened from the toolbar's file button. */
  docMenu: {
    width: 360,
    maxHeight: 320,
    radius: "12px",
    border: `1px solid ${palette.borderLight}`,
    shadow: "0 12px 36px rgba(33, 21, 71, 0.14)",
    padding: "6px",
    itemPadding: "9px 12px",
    itemRadius: "8px",
    itemGap: "10px",
    numWidth: 20,
    numFontSize: "11px",
    numFontWeight: 700,
    nameFontSize: "12.5px",
    nameFontWeight: 600,
    itemLineHeight: "normal",
    badgeFontSize: "10px",
    badgeFontWeight: 800,
    badgeRadius: "999px",
    badgePadding: "2px 8px",
  },

  /** Right-hand control rail. */
  rail: {
    /**
     * Content width. The design mock runs on the default `content-box`, so its
     * 360px sits *inside* the 1px left border (361px total). MUI's CssBaseline
     * makes everything `border-box`, which would eat a pixel off the rail and
     * shift the whole canvas — the rail restates `content-box` to match.
     */
    width: 360,
    boxSizing: "content-box" as const,
    background: palette.white,
    borderLeft: `1px solid ${palette.borderLight}`,
    padding: "22px 24px",
    sectionGap: "22px",

    labelFontSize: "13px",
    labelFontWeight: 800,
    labelColor: palette.ink,
  },

  /** "Highlight color" swatch row. */
  swatches: {
    blockGap: "11px",
    rowGap: "12px",
    size: 32,
    radius: "9px",
    restRing: `0 0 0 1px ${palette.border}`,
    /** Active ring is `0 0 0 2px #fff, 0 0 0 4px <swatch>` — built per swatch. */
    activeRingInner: `0 0 0 2px ${palette.white}`,
    activeRingWidth: "4px",
    checkSize: 15,

    hintFontSize: "11.5px",
    hintColor: palette.muted,
    hintLineHeight: 1.5,
  },

  /** Flag-count summary strip. */
  flagCard: {
    padding: "12px 14px",
    gap: "10px",
    background: palette.hover,
    border: `1px solid ${palette.borderLight}`,
    radius: "10px",

    iconBoxSize: 30,
    iconBoxRadius: "8px",
    iconBoxBg: palette.accentTint,
    iconSize: 15,
    iconColor: palette.accent,

    countFontSize: "15px",
    countFontWeight: 800,
    countColor: palette.ink,
    labelFontSize: "11px",
    labelColor: palette.meta,

    clearHeight: 30,
    clearPaddingX: "12px",
    clearRadius: "8px",
    clearBorderWidth: "1.5px",
    clearFontSize: "11.5px",
    clearFontWeight: 700,
  },

  /** "Common reasons" chips. */
  chips: {
    blockGap: "9px",
    rowGap: "8px",
    height: 32,
    paddingX: "13px",
    radius: "999px",
    borderWidth: "1.5px",
    fontSize: "12px",
    fontWeight: 700,
  },

  /** Free-text reason field. */
  reason: {
    blockGap: "6px",
    rows: 5,
    padding: "11px 13px",
    borderWidth: "1.5px",
    radius: "9px",
    fontSize: "13px",
    color: palette.ink,
    maxLength: 500,

    counterFontSize: "11px",
    counterFontWeight: 700,
    counterColor: palette.muted,
  },

  /** Destructive-consequence callout. */
  warning: {
    padding: "11px 14px",
    gap: "9px",
    background: palette.dangerTint,
    border: `1px solid ${palette.dangerBorder}`,
    radius: "10px",
    iconSize: 14,
    iconMarginTop: "1px",
    fontSize: "11.5px",
    lineHeight: 1.55,
  },

  /** Pinned rail footer. */
  footer: {
    padding: "16px 24px",
    gap: "10px",
    borderTop: `1px solid ${palette.borderLight}`,

    height: 44,
    radius: "9px",
    fontSize: "13.5px",
    fontWeight: 700,
    cancelPaddingX: "20px",
    cancelBorderWidth: "1.5px",
  },
};

export default declineRequestTokens;
